import React, { useState } from 'react';

import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import type { Lens } from '~/types/leLens';

import { lensAiContext } from './lensShareContent';
import type { LensUrlState } from './lensUrlState';

interface Props {
  lens: Lens;
  url: string;
  view?: LensUrlState;
  disabled?: boolean;
}

export default function LensShareExport({ lens, url, view, disabled }: Props) {
  const [preview, setPreview] = useState(false);
  const [includeSql, setIncludeSql] = useState(false);
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);
  const content = lensAiContext(lens, url, view, includeSql);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2">Use in another tool</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Paste a Lens link into Slack, Teams or email. Copy AI context for Claude, ChatGPT, Codex or Cursor.
      </Typography>
      <Button
        disabled={disabled}
        onClick={() => {
          setPreview(!preview);
          setFeedback(null);
        }}
        sx={{ mt: 1 }}
      >
        {preview ? 'Hide AI context' : 'Preview AI context'}
      </Button>
      {preview && (
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeSql}
                onChange={(_, checked) => {
                  setIncludeSql(checked);
                  setFeedback(null);
                }}
              />
            }
            label="Include dataset SQL"
          />
          <TextField
            label="AI context preview"
            value={content}
            multiline
            maxRows={10}
            fullWidth
            slotProps={{ input: { readOnly: true } }}
          />
          <Button
            disabled={disabled}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(content);
                setFeedback('copied');
              } catch {
                setFeedback('failed');
              }
            }}
          >
            Copy context for AI
          </Button>
          {feedback && (
            <Alert severity={feedback === 'copied' ? 'success' : 'error'}>
              {feedback === 'copied'
                ? 'Context copied. Paste it into your chosen AI tool.'
                : 'Copy failed. Select and copy the preview text manually.'}
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
